import React from 'react';
import { Link } from 'react-router-dom';
import { User, Crown, ChevronRight, BookOpen, Download, Bell, Settings, HelpCircle, LogOut, LogIn, Trophy, MessageSquare, Library, FileText, Award, Shield, Smartphone, KeyRound } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { usePlans } from '../hooks/usePlans';
import { useBrowserPush } from '../hooks/usePush';
import { toast } from '../hooks/use-toast';

const ADMIN_EMAILS = ['niranjanpujari31@gmail.com', 'gurusevakendra33@gmail.com'];

const menu = [
  { icon: BookOpen, label: 'ମୋର ପାଠ୍ୟ', en: 'My Lessons', to: '/bookmarks' },
  { icon: Trophy, label: 'Leaderboard', en: 'Top scorers', to: '/leaderboard' },
  { icon: MessageSquare, label: 'AI ସହାୟକ', en: 'Doubt Chat', to: '/chat' },
  { icon: Library, label: 'ଅଫିସିଆଲ ପୁସ୍ତକ', en: 'Official Resources', to: '/resources' },
  { icon: Bell, label: 'ସୂଚନା', en: 'Notifications', to: '/notifications' },
  { icon: Award, label: 'Refer & Earn', en: 'Invite friends, earn Premium', to: '/referral' },
  { icon: Smartphone, label: 'Play Store Publish Guide', en: 'How to publish app', to: '/publish-guide' },
  { icon: HelpCircle, label: 'ସାହାଯ୍ୟ', en: 'Help & Support', to: '/chat' },
];

export default function ProfilePage() {
  const { user, login, logout, loading } = useAuth();
  const { minPrice } = usePlans();
  const { permission, request: requestPush } = useBrowserPush();
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const togglePush = async () => {
    const p = await requestPush();
    if (p === 'granted') toast({ title: '🔔 Push enabled', description: 'ଏବେ ଆପଣଙ୍କୁ ନୂଆ update ପାଇଁ notification ମିଳିବ' });
    else if (p === 'denied') toast({ title: 'Push denied', description: 'Browser settings ରୁ allow କରନ୍ତୁ' });
    else if (p === 'unsupported') toast({ title: 'Browser support ନାହିଁ' });
  };

  return (
    <div className="px-4 pt-4 space-y-4">
      {/* Profile Header */}
      <Card className="rounded-3xl p-5 border-gray-200 shadow-sm">
        {loading ? (
          <div className="text-center text-sm text-gray-500 py-4">Loading...</div>
        ) : user ? (
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full shadow-md" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center text-2xl font-bold shadow-md">
                {user.name?.[0] || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-lg text-gray-900 truncate">{user.name}</div>
              <div className="text-xs text-gray-500 truncate">{user.email}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${user.is_premium ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  <Crown size={10} /> {user.is_premium ? 'Premium' : 'Free Plan'}
                </span>
                {isAdmin && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800"><Shield size={10} /> Admin</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-2">
            <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto flex items-center justify-center">
              <User size={30} className="text-gray-400" />
            </div>
            <div className="font-bold text-gray-800 mt-3">ଆପଣ ଲଗଇନ କରି ନାହାନ୍ତି</div>
            <p className="text-xs text-gray-500 mt-1">Google କିମ୍ବା ଇମେଲ୍ + ପାସୱାର୍ଡ ଦ୍ୱାରା ଲଗଇନ କରି quiz score, bookmark ଓ progress save କରନ୍ତୁ</p>
            <Button onClick={login} data-testid="profile-login-button" className="mt-3 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 w-full">
              <LogIn size={16} /> ଲଗଇନ / ଖାତା ଖୋଲନ୍ତୁ
            </Button>
          </div>
        )}
      </Card>

      {/* Push Notification Toggle */}
      {user && permission !== 'granted' && (
        <Card className="rounded-2xl p-4 border-cyan-200 bg-cyan-50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-500 text-white flex items-center justify-center"><Bell size={18} /></div>
          <div className="flex-1">
            <div className="font-bold text-sm text-cyan-900">Push Notifications enable କରନ୍ତୁ</div>
            <div className="text-[11px] text-cyan-700">ନୂଆ Mock test, alert ଓ update ପାଇବେ</div>
          </div>
          <button onClick={togglePush} className="bg-cyan-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">Allow</button>
        </Card>
      )}

      {!user?.is_premium && (
        <Link to="/payment">
          <Card className="rounded-2xl p-4 border-0 text-white shadow-md" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center"><Crown size={20} /></div>
              <div className="flex-1">
                <div className="font-bold">Premium ପାଆନ୍ତୁ</div>
                <div className="text-xs text-white/85">UPI ମାଧ୍ୟମରେ ₹{minPrice}/ମାସ ରୁ ଆରମ୍ଭ</div>
              </div>
              <ChevronRight size={20} />
            </div>
          </Card>
        </Link>
      )}

      {/* Admin Panel (only visible to admin) */}
      {isAdmin && (
        <Link to="/admin">
          <Card className="rounded-2xl p-4 border-0 text-white shadow-md" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center"><Shield size={20} /></div>
              <div className="flex-1">
                <div className="font-bold">Admin Panel</div>
                <div className="text-xs text-white/85">Payments approve · Notifications · Users</div>
              </div>
              <ChevronRight size={20} />
            </div>
          </Card>
        </Link>
      )}

      <Card className="rounded-2xl border-gray-200 overflow-hidden">
        {menu.map((m, i) => (
          <Link key={m.label} to={m.to}
            className={`flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors ${i !== menu.length - 1 ? 'border-b border-gray-100' : ''}`}>
            <m.icon size={18} className="text-gray-600" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-800">{m.label}</div>
              <div className="text-[10px] text-gray-500">{m.en}</div>
            </div>
            <ChevronRight size={16} className="text-gray-400" />
          </Link>
        ))}
      </Card>

      {user && (
        <Link to="/change-password" data-testid="profile-change-password-link"
          className="flex items-center gap-3 p-4 rounded-2xl border border-gray-200 hover:bg-gray-50 transition-colors">
          <KeyRound size={18} className="text-gray-600" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800">{user.auth_providers?.includes('password') ? 'ପାସୱାର୍ଡ ବଦଳାନ୍ତୁ' : 'ପାସୱାର୍ଡ ସେଟ୍ କରନ୍ତୁ'}</div>
            <div className="text-[10px] text-gray-500">{user.auth_providers?.includes('password') ? 'Change password' : 'Login without Google using email + password'}</div>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </Link>
      )}

      {user && (
        <button onClick={logout} data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 p-3.5 rounded-2xl bg-red-50 text-red-600 font-semibold text-sm hover:bg-red-100 transition-colors">
          <LogOut size={16} /> ଲଗଆଉଟ
        </button>
      )}

      <div className="text-center text-[11px] text-gray-400 pt-1 pb-2">
        Version 1.0.0 · © 2025 Gurusevakendra · Made with ♥ in Odisha
      </div>
    </div>
  );
}

